import { API, postJSON } from "../../api.js";
import { $, cls, esc } from "../../ui/format.js";
import { helpIcon } from "../../ui/help.js";
import { toast } from "../../ui/modals.js";

// ---------------------------------------------------------------- Board of Directors & IPO

function _confidenceClass(verdict) {
  if (verdict === "Strong Confidence") return "strong";
  if (verdict === "Satisfied") return "satisfied";
  if (verdict === "Concerned") return "concerned";
  return "crisis"; // Considering Intervention | Crisis
}

const BOARD_FOCUS_AREAS = ["Growth Strategy", "Cost Discipline", "Cash Position", "Long-term Vision"];

async function renderBoard(main, snap) {
  main.innerHTML = `
    <h2 class="panel-title">Board of Directors — Month ${snap.month} ${helpIcon("board")}</h2>
    <div class="plain-english-card" id="board-plain-english">Loading…</div>
    <div id="board-gauge">Loading…</div>
    <div class="deal-actions" id="board-focus-buttons" style="margin: 16px 0 22px; flex-wrap: wrap;"></div>
    <div class="deal-actions" style="margin-bottom:24px;">
      <button id="btn-ipo">Pursue IPO</button>
    </div>
    <h3 style="font-family:var(--font-display); font-size:17px; margin:8px 0 12px;">Quarterly Review History</h3>
    <div id="board-meetings">Loading…</div>
    <h3 style="font-family:var(--font-display); font-size:17px; margin:24px 0 12px;">Succession</h3>
    <div id="succession-panel">Loading…</div>
  `;

  const bs = await fetch(`${API}/board`).then((x) => x.json());
  const cls = _confidenceClass(bs.verdict);
  const monthsToReview = 3 - (snap.month % 3 === 0 ? 0 : snap.month % 3);

  $("board-plain-english").innerHTML =
    `The Board is feeling <strong style="color:var(--text-hi);">${esc(bs.verdict.toLowerCase())}</strong> about you right now
     (${bs.board_confidence}/100). ${bs.intervention_streak >= 2
       ? `⚠ You're ${bs.intervention_streak} crisis-level review${bs.intervention_streak > 1 ? "s" : ""} deep — one more and there are real consequences.`
       : bs.intervention_streak === 1
         ? "One crisis-level review so far — a second in a row triggers mandatory cost discipline."
         : "No crisis pressure right now."
     } Next review is in ${monthsToReview === 0 ? 3 : monthsToReview} month${monthsToReview === 1 ? "" : "s"}.
     ${bs.is_public ? `Public since month ${bs.ipo_month}.` : ""}`;

  $("board-gauge").innerHTML = `
    <div class="risk-stat-row">
      <div class="risk-stat-box">
        <div class="risk-stat-label">Board Confidence</div>
        <div class="risk-stat-value">${bs.board_confidence}<span style="font-size:14px;color:var(--text-low)">/100</span></div>
        <div class="confidence-gauge-track"><div class="confidence-gauge-fill ${cls}" style="width:${bs.board_confidence}%"></div></div>
        <div style="font-family:var(--font-mono); font-size:12px; color:var(--text-mid);">${esc(bs.verdict)}</div>
      </div>
      <div class="risk-stat-box">
        <div class="risk-stat-label">Company Status</div>
        <div class="risk-stat-value" style="font-size:18px;">${bs.is_public ? `Public (M${bs.ipo_month})` : "Private"}</div>
      </div>
      ${bs.intervention_streak > 0 ? `
      <div class="risk-stat-box" style="border-color:var(--oxblood);">
        <div class="risk-stat-label" style="color:var(--oxblood);">Crisis Streak</div>
        <div class="risk-stat-value" style="color:var(--oxblood);">${bs.intervention_streak}</div>
      </div>` : ""}
      ${bs.is_public && bs.activist_pressure_streak > 0 ? `
      <div class="risk-stat-box" style="border-color:var(--brass);">
        <div class="risk-stat-label" style="color:var(--brass);">Activist Pressure</div>
        <div class="risk-stat-value" style="color:var(--brass);">${bs.activist_pressure_streak}/3 missed quarters</div>
      </div>` : ""}
    </div>
  `;
  if (bs.is_public && bs.activist_pressure_streak > 0) {
    $("board-gauge").innerHTML += `
      <div class="plain-english-card" style="margin-top:10px; border-left-color:var(--brass);">
        The market has missed its own trailing-quarter revenue bar ${bs.activist_pressure_streak} month${bs.activist_pressure_streak > 1 ? "s" : ""} running.
        A third consecutive miss triggers real activist investor pressure — a forced buyback plus a sentiment and morale hit.
      </div>
    `;
  }
  if (bs.last_activist_pressure_month) {
    $("board-gauge").innerHTML += `
      <div style="font-size:12px; color:var(--text-low); margin-top:8px;">Last activist pressure event: Month ${bs.last_activist_pressure_month}</div>
    `;
  }

  $("board-focus-buttons").innerHTML = BOARD_FOCUS_AREAS.map((f) =>
    `<button data-focus="${esc(f)}">Address Board: ${esc(f)}</button>`
  ).join("");
  $("board-focus-buttons").querySelectorAll("[data-focus]").forEach((btn) => {
    btn.addEventListener("click", async () => {
      const res = await postJSON("/board/address", { focus: btn.dataset.focus });
      await window.refreshAll();
      toast(res.message);
    });
  });

  $("btn-ipo").addEventListener("click", async () => {
    const res = await postJSON("/board/ipo", {});
    await window.refreshAll();
    toast(res.message);
  });
  if (bs.is_public) {
    $("btn-ipo").disabled = true;
    $("btn-ipo").textContent = "Already Public";
  }

  $("board-meetings").innerHTML = bs.meetings.length ? bs.meetings.map((m) => `
    <div class="board-meeting-row">
      <span>Month ${m.month} — ${esc(m.verdict)} (${m.confidence_score}/100)</span>
      <span>Growth ${m.revenue_growth_pct > 0 ? "+" : ""}${m.revenue_growth_pct}% · Margin ${m.net_margin_pct > 0 ? "+" : ""}${m.net_margin_pct}% · Runway ${m.cash_runway_months}mo · Leverage ${m.leverage}x</span>
    </div>
  `).join("") : `<p style="color:var(--text-mid)">No quarterly reviews yet — the first is at month 3.</p>`;

  await renderSuccession();
}

async function renderSuccession() {
  const host = document.getElementById("succession-panel");
  if (!host) return;
  host.innerHTML = "Loading…";
  const s = await fetch(`${API}/succession`).then((x) => x.json());
  const ceo = s.current_ceo;

  let html = `
    <div class="risk-stat-row">
      <div class="risk-stat-box">
        <div class="risk-stat-label">${_ordinal(ceo.generation)}-Gen CEO</div>
        <div class="risk-stat-value" style="font-size:17px;">${esc(ceo.name)}</div>
        <div style="font-family:var(--font-mono); font-size:12px; color:var(--text-mid);">${esc(ceo.archetype)} · since M${ceo.started_month}</div>
      </div>
      <div class="risk-stat-box">
        <div class="risk-stat-label">Vision / Ops / Deals / Stewardship</div>
        <div class="risk-stat-value" style="font-size:15px;">${ceo.vision} / ${ceo.operations} / ${ceo.dealmaking} / ${ceo.stewardship}</div>
      </div>
    </div>
  `;

  if (s.candidate) {
    const c = s.candidate;
    const monthsLeft = Math.max(0, 3 - c.months_groomed);
    html += `
      <div class="plain-english-card" style="margin-top:12px;">
        <strong style="color:var(--text-hi);">${esc(c.name)}</strong> (${esc(c.archetype)}) is being groomed as a successor —
        ${c.months_groomed} month${c.months_groomed === 1 ? "" : "s"} in.
        Vision ${c.vision} / Ops ${c.operations} / Deals ${c.dealmaking} / Stewardship ${c.stewardship}.
        ${c.can_appoint ? "" : ` Needs at least ${monthsLeft} more month${monthsLeft === 1 ? "" : "s"} before appointment.`}
      </div>
      <div class="deal-actions" style="margin-top:10px;">
        <button id="btn-appoint-successor" ${c.can_appoint ? "" : "disabled"}>Appoint as CEO</button>
        <button id="btn-dismiss-successor" class="ghost">Dismiss Candidate</button>
      </div>
    `;
  } else {
    html += `
      <div class="deal-actions" style="margin-top:12px;">
        <button id="btn-groom-successor">Groom a Successor</button>
      </div>
    `;
  }

  if (s.history.length > 1) {
    html += `
      <h4 style="font-family:var(--font-display); font-size:14px; margin:18px 0 8px; color:var(--text-mid);">Leadership History</h4>
      ${s.history.slice().reverse().map((h) => `
        <div class="board-meeting-row">
          <span>${_ordinal(h.generation)} Gen — ${esc(h.ceo_name)} (${esc(h.archetype)})</span>
          <span>M${h.started_month}${h.ended_month !== null ? `–M${h.ended_month}` : " (current)"}${h.era_score !== null ? ` · Era Score ${h.era_score}/100` : ""}</span>
        </div>
      `).join("")}
    `;
  }

  host.innerHTML = html;

  const groomBtn = document.getElementById("btn-groom-successor");
  if (groomBtn) groomBtn.addEventListener("click", async () => {
    const res = await postJSON("/succession/groom", {});
    toast(res.message);
    await renderSuccession();
  });
  const appointBtn = document.getElementById("btn-appoint-successor");
  if (appointBtn) appointBtn.addEventListener("click", async () => {
    const res = await postJSON("/succession/appoint", {});
    toast(res.message);
    await window.refreshAll();
  });
  const dismissBtn = document.getElementById("btn-dismiss-successor");
  if (dismissBtn) dismissBtn.addEventListener("click", async () => {
    const res = await postJSON("/succession/dismiss", {});
    toast(res.message);
    await renderSuccession();
  });
}

function _ordinal(n) {
  const suffixes = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]);
}

// Late-bound: a later view may replace these (window.X = ...), so callers use window.X.
window.renderBoard = renderBoard;

export { renderSuccession };
